import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, ArrowLeft, Sparkles, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { streamChat, submitSupportLead } from "@/lib/api";
import { useSite } from "@/context/SiteContent";
import { scrollToId } from "@/lib/scroll";

const SESSION_KEY = "tw_chat_session";
const NUDGE_KEY = "tw_chat_nudge_dismissed";
const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem(SESSION_KEY, s); }
  return s;
};

const WELCOME = "Hi, I'm Ask Scottie, the Campus2Corp assistant. I can explain the 5Ps, the eBook and the booking, or help if you're stuck with a payment or download. What can I help you with?";
const SUGGESTIONS = ["What are the 5Ps?", "What's inside the eBook?", "I paid but did not get my eBook", "How do I book a 5Ps Conversation?"];

const stripBook = (t) => t.replace(/\[\[BOOK\]\]/g, "").replace(/\[\[?B?O?O?K?\]?\]?\s*$/, "").trimEnd();
const renderRich = (text) => String(text).split("\n").map((line, li) => {
  const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return (
    <span key={li} className="scottie-line">
      {li > 0 && <br />}
      {parts.map((p, pi) => (p.startsWith("**") && p.endsWith("**"))
        ? <strong key={pi}>{p.slice(2, -2)}</strong>
        : <span key={pi}>{p}</span>)}
    </span>
  );
});

const Bubble = ({ role, text, onBook, bookLabel }) => {
  const showBook = role === "assistant" && /\[\[BOOK\]\]/.test(text);
  const display = role === "assistant" ? stripBook(text) : text;
  return (
    <div className={`scottie-msg scottie-msg-${role}`} data-testid={`scottie-msg-${role}`}>
      {role === "assistant" && <span className="scottie-msg-badge">Scottie</span>}
      <div className="scottie-msg-text" data-testid="scottie-msg-body">{renderRich(display || (role === "assistant" ? "…" : ""))}</div>
      {showBook && (
        <button className="scottie-inline-book" onClick={onBook} data-testid="scottie-inline-book">
          <CalendarDays size={14} /> {bookLabel || "Book a 5Ps Conversation"}
        </button>
      )}
    </div>
  );
};

const LeadForm = ({ onBack, sessionId }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) return;
    setBusy(true);
    try {
      await submitSupportLead({ session_id: sessionId, name: name.trim(), email: email.trim(), message: message.trim() });
      setDone(true);
    } catch { toast.error("Could not send that. Please try again."); }
    finally { setBusy(false); }
  };
  if (done) return (
    <div className="scottie-lead-done" data-testid="scottie-lead-done">
      <div className="scottie-lead-check">✓</div>
      <h4>Thank you.</h4>
      <p>Scott will follow up at <strong>{email}</strong> personally. You can keep chatting with me any time.</p>
      <button className="scottie-lead-back" onClick={onBack} data-testid="scottie-lead-back-chat">Back to chat</button>
    </div>
  );
  return (
    <form className="scottie-lead" onSubmit={submit} data-testid="scottie-lead-form">
      <button type="button" className="scottie-lead-return" onClick={onBack} data-testid="scottie-lead-back"><ArrowLeft size={15} /> Back to chat</button>
      <h4>Ask Scott to follow up</h4>
      <p className="scottie-lead-hint">Leave your details and Scott will help you personally.</p>
      <label><span>Name</span><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="scottie-lead-name" /></label>
      <label><span>Email *</span><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="scottie-lead-email" /></label>
      <label><span>How can Scott help? *</span><textarea required rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="For example: I paid but did not receive my eBook." data-testid="scottie-lead-message" /></label>
      <button className="btn btn-gold btn-block" type="submit" disabled={busy} data-testid="scottie-lead-submit">{busy ? "Sending..." : "Send to Scott"}</button>
    </form>
  );
};

export const AskScottie = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat");
  const [messages, setMessages] = useState([{ role: "assistant", text: WELCOME }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [nudge, setNudge] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSession()).current;
  const { bookingCta } = useSite();

  const bookCall = () => {
    setOpen(false);
    if (typeof document !== "undefined" && document.getElementById("booking")) scrollToId("booking");
    else window.location.assign("/#booking");
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, view]);

  useEffect(() => {
    if (sessionStorage.getItem(NUDGE_KEY)) return undefined;
    const t = setTimeout(() => setNudge(true), 6000);
    return () => clearTimeout(t);
  }, []);

  const dismissNudge = () => { setNudge(false); sessionStorage.setItem(NUDGE_KEY, "1"); };
  const openChat = () => { setOpen(true); dismissNudge(); };

  const sendRef = useRef(null);
  useEffect(() => {
    const h = (e) => {
      setOpen(true); setView("chat"); setNudge(false); sessionStorage.setItem(NUDGE_KEY, "1");
      const msg = e.detail?.message;
      if (msg) setTimeout(() => sendRef.current?.(msg), 150);
    };
    window.addEventListener("scottie:open", h);
    return () => window.removeEventListener("scottie:open", h);
  }, []);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: content }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      await streamChat(sessionId, content, {
        onDelta: (d) => setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + d };
          return copy;
        }),
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Sorry, I could not respond just now. Please try again, or use \"Ask Scott to follow up\" below." };
        return copy;
      });
    } finally { setBusy(false); }
  };
  sendRef.current = send;

  return (
    <>
      {nudge && !open && (
        <div className="scottie-nudge" data-testid="scottie-nudge">
          <button className="scottie-nudge-close" onClick={dismissNudge} aria-label="Dismiss" data-testid="scottie-nudge-close"><X size={13} /></button>
          <button className="scottie-nudge-body" onClick={openChat} data-testid="scottie-nudge-open">
            <span className="scottie-nudge-title">Questions about the eBook or 5Ps?</span>
            <span className="scottie-nudge-sub">Ask Scottie, I'm here to help.</span>
          </button>
        </div>
      )}
      <button className={`scottie-launcher${open ? " is-open" : ""}`} onClick={() => { setOpen((o) => !o); dismissNudge(); }} aria-label="Open Ask Scottie" data-testid="scottie-launcher">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && <span className="scottie-launcher-label">Ask Scottie</span>}
      </button>

      {open && (
        <div className="scottie-panel" data-testid="scottie-panel">
          <div className="scottie-head">
            <div className="scottie-head-avatar"><Sparkles size={18} /></div>
            <div className="scottie-head-copy">
              <strong>Ask Scottie</strong>
              <small><span className="scottie-online" /> Campus2Corp assistant</small>
            </div>
            <button className="scottie-head-close" onClick={() => setOpen(false)} aria-label="Close" data-testid="scottie-close"><X size={18} /></button>
          </div>

          {view === "lead" ? (
            <LeadForm sessionId={sessionId} onBack={() => setView("chat")} />
          ) : (
            <>
              <div className="scottie-body" ref={scrollRef} data-testid="scottie-messages">
                {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.text} onBook={bookCall} bookLabel={bookingCta} />)}
                {messages.length <= 1 && (
                  <div className="scottie-suggestions" data-testid="scottie-suggestions">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => send(s)} data-testid="scottie-suggestion">{s}</button>
                    ))}
                  </div>
                )}
              </div>
              <div className="scottie-foot">
                <div className="scottie-quickactions">
                  <button className="scottie-book-btn" onClick={bookCall} data-testid="scottie-book-call"><CalendarDays size={15} /> {bookingCta || "Book a call with Scott"}</button>
                  <button className="scottie-lead-open" onClick={() => setView("lead")} data-testid="scottie-open-lead">Ask Scott to follow up</button>
                </div>
                <form className="scottie-input-row" onSubmit={(e) => { e.preventDefault(); send(); }}>
                  <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question..." disabled={busy} data-testid="scottie-input" />
                  <button type="submit" className="scottie-send" disabled={busy || !input.trim()} aria-label="Send" data-testid="scottie-send"><Send size={17} /></button>
                </form>
                <div className="scottie-disclaimer">Educational content only. Not financial advice.</div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
